/**
 * Composable pour les handlers de changement de formation
 * Limite: 100 lignes max
 */
import { isRef } from 'vue'
import { getFormationLieuOptions } from './useFormationLieu.js'

/**
 * @param {Function} getSelectedFormation - obligatoire : la liste déroulante portant
 *   l'id du catalogue, c'est la seule source qui permette de réaligner formation_nom.
 */
export function useFormationHandlers(formOrRef, getFormValue, getFormationMessage, getSelectedFormation) {
  // Support both ref and plain object for backwards compatibility
  const getForm = () => isRef(formOrRef) ? formOrRef.value : formOrRef

  /**
   * La formule appartient à une formation : changer de formation ou de
   * catégorie la rend caduque. Sans ce détachement, l'ancien identifiant
   * survivrait dans le formulaire — invisible, puisque les listes de durée ne
   * s'affichent que pour les formules de la formation courante — et le devis
   * imprimerait la durée d'une formule d'une autre formation.
   */
  const detacherFormule = (form) => {
    if (form.formation_formule_id !== undefined) {
      form.formation_formule_id = null
    }
  }

  const onCategoryChange = () => {
    const form = getForm()
    form.formation_catalogue_id = ''
    form.formation_nom = ''
    form.formation_lieu = ''
    detacherFormule(form)
    if (form.formation_message !== undefined) {
      form.formation_message = ''
    }
  }

  /**
   * Un lieu déjà renseigné est conservé s'il reste proposé par la nouvelle formation :
   * basculer d'une déclinaison de durée à l'autre ne doit pas vider le lieu et bloquer
   * l'enregistrement. INTER fait exception, ses sessions et villes étant propres à
   * une formation donnée.
   */
  const keepLieuIfStillValid = (form, formation) => {
    const lieu = form.formation_lieu
    if (!lieu || lieu === 'INTER') return ''
    const options = getFormationLieuOptions(formation, form.client_type)
    return options.some(option => option.value === lieu) ? lieu : ''
  }

  const onFormationChange = () => {
    const form = getForm()
    // La liste déroulante porte l'identifiant catalogue (le nom n'est pas unique) :
    // on réaligne le nom stocké sur la formation réellement choisie.
    const formation = form.formation_catalogue_id ? getSelectedFormation() : null
    detacherFormule(form)
    if (formation) {
      form.formation_nom = formation.NOM
    } else {
      // Sélection vidée, ou id ne correspondant à aucune formation proposée : on ne
      // conserve pas un id orphelin, que le serveur accepterait avec un nom vide.
      form.formation_catalogue_id = ''
      form.formation_nom = ''
    }
    if (getFormationMessage) {
      const message = getFormationMessage()
      if (message && form.formation_message !== undefined) {
        form.formation_message = message
      } else if (form.formation_message !== undefined) {
        form.formation_message = ''
      }
    }
    form.formation_lieu = keepLieuIfStillValid(form, formation)
  }

  const onLieuChange = () => {
    const form = getForm()
    const lieu = form.formation_lieu
    if (lieu === 'INTER') {
      if (form.lieu_formation_adresse !== undefined) {
        form.lieu_formation_adresse = ''
        form.lieu_formation_code_postal = ''
        form.lieu_formation_ville = ''
        form.lieu_formation_identique_adresse = false
      }
    } else {
      if (form.inter_ville !== undefined) {
        form.inter_ville = ''
        form.inter_session_id = ''
        form.inter_nombre_participants = null
        form.inter_tarif = null
      }
    }
  }

  const onVilleChange = () => {
    const form = getForm()
    if (form.inter_session_id !== undefined) {
      form.inter_session_id = ''
      form.inter_nombre_participants = null
      form.inter_tarif = null
    }
  }

  return {
    onCategoryChange,
    onFormationChange,
    onLieuChange,
    onVilleChange
  }
}
